// 2026-09-21 - FormuLearning - My Projects 폴더 탐색기 (이슈 #77 G6, COMP-PROJECT-EXPLORER)
// 2026-11-03 수정 - 이슈 #114: viewer 는 생성·이름 변경·이동·삭제 메뉴와 끌어 옮기기 비활성(Open·접기·정렬은 유지).
// 파일 위치: frontend/src/features/project/ProjectExplorer.tsx
//
// 좌측 패널 My Projects 를 폴더 트리로 보인다(M901-04 · REQ-UX-33·34). 규칙 정본 = 프로세스
// 설계서 §3.3 BR-PRJ-01~09, 화면 = 정본개정안 §6.3.
//   · 폴더 — 사용자 생성 · 중첩 · 접기/펼치기(브라우저 보존) · 현재 프로젝트가 든 폴더는 자동 펼침
//   · 이동 — 드래그(프로젝트·폴더). 폴더 행 = 그 안으로 · 프로젝트 행 = 그 프로젝트의 폴더로 ·
//            머리·빈 곳 = 루트로. 자기 자신·하위로는 놓을 수 없다(BR-PRJ-06). 대체 경로 = 우클릭 Move to
//   · 우클릭 — 루트: New Folder·New Project / 폴더: New Project Here·New Subfolder·Rename·Move to·Delete /
//              프로젝트: Open·Rename·Move to
//   · 이름 변경 — 행 안에서 바로(Enter 저장 · Esc 취소 · 포커스 이탈 저장 · 빈 값 = 취소)
// 규칙 위반(이름 중복·순환·승계 충돌)은 서버 409 문장을 토스트로 보이고 화면은 서버 상태를 따른다.
// 숨긴 프로젝트는 트리에 넣지 않는다(BR-PRJ-08) — 호출부가 children 으로 «Show hidden» 구획을 넘긴다.
// 2026-09-23 수정 - 이슈 #82: 진행 중 작업이 있으면 New Project(우클릭 2종)를 막고 안내한다 — 생성 즉시 현재 프로젝트가 바뀐다.
//   프로젝트 열기는 호출부(`LeftPanel.openProject`)가 막는다. 폴더·이름·이동 정리는 현재 프로젝트와 무관해 열어 둔다.

import { useMemo, useRef, useState, type ReactNode } from 'react'
import ContextMenu, { type MenuItem } from '../../components/common/ContextMenu'
import { useProjectStore } from '../../stores/projectStore'
import { useUiStore } from '../../stores/uiStore'
import { toast } from '../../stores/toastStore'
import { blockIfBusy } from '../../stores/busyStore'
import { apiErrorMessage } from '../../lib/apiError'
import type { ProjectListItem } from '../../types/project'
import ProjectSortControl from './ProjectSortControl'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'
import {
  ancestorIds, compareProjects, countProjects, folderPath, indexTree, loadSort, moveTargets, saveSort, subtreeIds,
  type SortState,
} from './folderTree'

interface Props {
  /** 숨기지 않은 프로젝트 */
  projects: ProjectListItem[]
  onOpenProject: (id: string) => void
  /** 트리 아래에 붙일 구획(«Show hidden») */
  children?: ReactNode
}

type Item = { kind: 'folder' | 'project'; id: string }
type Editing = Item | { kind: 'new-folder'; parentId: string | null } | null
type Menu = { x: number; y: number; items: MenuItem[] } | null

const COLLAPSED_KEY = 'formulearn.folderCollapsed'
const ROOT = '__root__'
const indent = (depth: number) => 8 + depth * 10

function loadCollapsed(): Set<string> {
  try {
    const v = JSON.parse(localStorage.getItem(COLLAPSED_KEY) || '[]')
    return new Set(Array.isArray(v) ? v : [])
  } catch {
    return new Set()
  }
}

/** 행 안 이름 입력. Enter·포커스 이탈 = 확정, Esc = 취소. 확정은 한 번만 일어난다. */
function InlineName({ initial, maxLength, onCommit, onCancel }: {
  initial: string
  maxLength: number
  onCommit: (v: string) => void
  onCancel: () => void
}) {
  const [value, setValue] = useState(initial)
  const done = useRef(false)
  const finish = (commit: boolean) => {
    if (done.current) return
    done.current = true
    if (commit) onCommit(value)
    else onCancel()
  }
  return (
    <input
      aria-label="Name"
      data-testid="inline-name"
      value={value}
      autoFocus
      maxLength={maxLength}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') finish(true)
        if (e.key === 'Escape') finish(false)
      }}
      onBlur={() => finish(true)}
      onClick={(e) => e.stopPropagation()}
      className="min-w-0 flex-1 rounded border bg-bg-input px-1 py-0.5 text-xs text-text-body outline-none"
      style={{ borderColor: 'var(--color-accent-purple)' }}
    />
  )
}

export default function ProjectExplorer({ projects, onOpenProject, children }: Props) {
  const folders = useProjectStore((s) => s.folders)
  const allProjects = useProjectStore((s) => s.projects)
  const currentProject = useProjectStore((s) => s.currentProject)
  const { updateProjectInfo, moveProject, createFolder, renameFolder, moveFolder, deleteFolder } = useProjectStore.getState()
  const openNewProject = useUiStore((s) => s.openNewProject)
  // viewer(#114) — 생성·이름 변경·이동·삭제 메뉴와 끌어 옮기기를 막는다. Open·접기·정렬은 탐색이라 그대로.
  const canWrite = useCanWrite()
  const ro = !canWrite

  const [sort, setSortState] = useState<SortState>(loadSort)
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsed)
  const [editing, setEditing] = useState<Editing>(null)
  const [menu, setMenu] = useState<Menu>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const [drag, setDrag] = useState<Item | null>(null)

  const index = useMemo(() => indexTree(folders, projects), [folders, projects])
  const cmp = useMemo(() => compareProjects(sort), [sort])

  const setSort = (next: SortState) => {
    setSortState(next)
    saveSort(next)
  }
  const writeCollapsed = (next: Set<string>) => {
    setCollapsed(next)
    try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next])) } catch { /* 무시 */ }
  }
  const toggle = (id: string) => {
    const next = new Set(collapsed)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    writeCollapsed(next)
  }
  const expand = (ids: (string | null)[]) => {
    const hit = ids.filter((id): id is string => !!id && collapsed.has(id))
    if (!hit.length) return
    const next = new Set(collapsed)
    hit.forEach((id) => next.delete(id))
    writeCollapsed(next)
  }

  // 현재 프로젝트가 든 폴더와 그 상위는 펼친다 — 열어 둔 프로젝트가 접힌 폴더 속에 숨지 않게.
  // 프로젝트·소속 폴더·폴더 목록이 바뀐 렌더에서 한 번 조정한다(effect 안 setState 대신 렌더 중 조정).
  // 저장소에는 쓰지 않는다 — 사용자가 직접 접고 편 상태만 보존한다.
  const autoKey = `${currentProject?.id ?? ''}|${currentProject?.folder_id ?? ''}|${folders.length}`
  const [autoFor, setAutoFor] = useState('')
  if (autoKey !== autoFor) {
    setAutoFor(autoKey)
    const hit = ancestorIds(index.byId, currentProject?.folder_id ?? null).filter((id) => collapsed.has(id))
    if (hit.length) setCollapsed(new Set([...collapsed].filter((id) => !hit.includes(id))))
  }

  const run = async (work: () => Promise<unknown>, fallback: string) => {
    try {
      await work()
    } catch (e) {
      toast(apiErrorMessage(e, fallback), 'warn')
    }
  }

  // ── 이동 ────────────────────────────────────────────────────────
  const parentOf = (it: Item): string | null =>
    it.kind === 'project'
      ? (projects.find((p) => p.id === it.id)?.folder_id ?? null)
      : (index.byId.get(it.id)?.parent_id ?? null)

  const canMove = (it: Item, target: string | null) =>
    !(it.kind === 'folder' && target !== null && subtreeIds(index, it.id).has(target))

  const move = async (it: Item, target: string | null) => {
    if (!canMove(it, target) || parentOf(it) === target) return
    await run(
      () => (it.kind === 'project' ? moveProject(it.id, target) : moveFolder(it.id, target)),
      '옮기지 못했습니다.',
    )
    expand([target])
  }

  const dragProps = (it: Item) => ({
    draggable: canWrite && !(editing && 'id' in editing && editing.id === it.id),
    onDragStart: (e: React.DragEvent) => {
      setDrag(it)
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', it.id)
    },
    onDragEnd: () => {
      setDrag(null)
      setDropTarget(null)
    },
  })

  /**
   * target: 폴더 id · null(루트). 놓을 수 있을 때만 기본 동작을 막아 드롭을 허용한다.
   * 놓을 수 없는 행에서도 전파는 끊는다 — 그러지 않으면 바깥 컨테이너(루트)가 받아 «루트로 이동» 이 된다.
   */
  const dropProps = (target: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      const it = drag
      if (!it) return
      e.stopPropagation()
      if (!canMove(it, target)) {
        setDropTarget(null)
        return
      }
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDropTarget(target ?? ROOT)
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const it = drag
      setDrag(null)
      setDropTarget(null)
      if (it) move(it, target)
    },
  })

  // ── 우클릭 메뉴 ──────────────────────────────────────────────────
  const openMenu = (e: React.MouseEvent, items: MenuItem[]) => {
    e.preventDefault()
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY, items })
  }

  const moveMenu = (x: number, y: number, it: Item, label: string) => {
    const here = parentOf(it)
    const items: MenuItem[] = [{ label: `Move “${label}” to`, disabled: true }, { divider: true, label: '' }]
    for (const t of moveTargets(index)) {
      items.push({
        label: t.id === null ? t.label : `📁 ${t.label}`,
        depth: t.depth,
        disabled: t.id === here || !canMove(it, t.id),
        onSelect: () => move(it, t.id),
      })
    }
    setMenu({ x, y, items })
  }

  const rootMenu = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-hidden-section]')) return
    openMenu(e, [
      { label: 'New Folder', disabled: ro, onSelect: () => setEditing({ kind: 'new-folder', parentId: null }) },
      { label: 'New Project', disabled: ro, onSelect: () => blockIfBusy() || openNewProject(null) },
    ])
  }

  const folderMenu = (e: React.MouseEvent, id: string, name: string) => {
    const { clientX: x, clientY: y } = e
    openMenu(e, [
      { label: 'New Project Here', disabled: ro, onSelect: () => blockIfBusy() || openNewProject(id) },
      {
        label: 'New Subfolder',
        disabled: ro,
        onSelect: () => {
          expand([id])
          setEditing({ kind: 'new-folder', parentId: id })
        },
      },
      { label: 'Rename', disabled: ro, onSelect: () => setEditing({ kind: 'folder', id }) },
      { label: 'Move to…', disabled: ro, onSelect: () => moveMenu(x, y, { kind: 'folder', id }, name) },
      { divider: true, label: '' },
      { label: 'Delete Folder', danger: true, disabled: ro, onSelect: () => removeFolder(id) },
    ])
  }

  const projectMenu = (e: React.MouseEvent, p: ProjectListItem) => {
    const { clientX: x, clientY: y } = e
    openMenu(e, [
      { label: 'Open', onSelect: () => onOpenProject(p.id) },
      { label: 'Rename', disabled: ro, onSelect: () => setEditing({ kind: 'project', id: p.id }) },
      { label: 'Move to…', disabled: ro, onSelect: () => moveMenu(x, y, { kind: 'project', id: p.id }, p.name) },
    ])
  }

  // ── 폴더 삭제 (BR-PRJ-07) ─────────────────────────────────────────
  const removeFolder = (id: string) => {
    const f = index.byId.get(id)
    if (!f) return
    const subs = index.children.get(id)?.length ?? 0
    const inside = allProjects.filter((p) => p.folder_id === id).length // 숨긴 것 포함 — 서버가 함께 옮긴다
    const dest = folderPath(index.byId, f.parent_id) || 'My Projects (root)'
    const ok = window.confirm(
      `Delete folder "${f.name}"?\n\n${subs} subfolder(s) and ${inside} project(s) will move to "${dest}".\nProjects are not deleted.`,
    )
    if (!ok) return
    run(async () => {
      const r = await deleteFolder(id)
      toast(`폴더 「${f.name}」를 삭제했습니다 — 하위 폴더 ${r.moved_folders}개·프로젝트 ${r.moved_projects}건을 「${dest}」로 옮겼습니다.`)
    }, '폴더를 삭제하지 못했습니다.')
  }

  // ── 이름 확정 ────────────────────────────────────────────────────
  const commitName = async (value: string) => {
    const ed = editing
    setEditing(null)
    const name = value.trim()
    if (!ed || !name) return
    if (ed.kind === 'new-folder') {
      await run(() => createFolder(name, ed.parentId), '폴더를 만들지 못했습니다.')
      expand([ed.parentId])
    } else if (ed.kind === 'folder') {
      if (index.byId.get(ed.id)?.name === name) return
      await run(() => renameFolder(ed.id, name), '폴더 이름을 바꾸지 못했습니다.')
    } else {
      if (projects.find((p) => p.id === ed.id)?.name === name) return
      await run(() => updateProjectInfo(ed.id, { name }), '프로젝트 이름을 바꾸지 못했습니다.')
    }
  }

  // ── 렌더 ────────────────────────────────────────────────────────
  const rowCls = 'mb-0.5 flex w-full cursor-pointer items-center gap-1.5 rounded py-1 pr-1.5 text-left text-xs transition-colors hover:bg-bg-input'
  const dropStyle = (active: boolean) =>
    active ? { boxShadow: 'inset 0 0 0 1.5px var(--color-accent-purple)', background: 'var(--bg-chip-purple)' } : {}

  const renderLevel = (parentId: string | null, depth: number): ReactNode[] => {
    const out: ReactNode[] = []
    if (editing?.kind === 'new-folder' && editing.parentId === parentId) {
      out.push(
        <div key={`new-${parentId ?? ROOT}`} className={rowCls} style={{ paddingLeft: indent(depth) }}>
          <span className="w-3" />
          <span>📁</span>
          <InlineName initial="New Folder" maxLength={100} onCommit={commitName} onCancel={() => setEditing(null)} />
        </div>,
      )
    }
    for (const f of index.children.get(parentId) ?? []) {
      const open = !collapsed.has(f.id)
      const isEditing = editing?.kind === 'folder' && editing.id === f.id
      out.push(
        <div
          key={f.id}
          role="treeitem"
          aria-expanded={open}
          data-folder-id={f.id}
          title={f.name}
          onClick={() => !isEditing && toggle(f.id)}
          onContextMenu={(e) => folderMenu(e, f.id, f.name)}
          {...dragProps({ kind: 'folder', id: f.id })}
          {...dropProps(f.id)}
          className={rowCls}
          style={{ paddingLeft: indent(depth), ...dropStyle(dropTarget === f.id) }}
        >
          <span className="w-3 shrink-0 text-[9px] text-text-secondary">{open ? '▾' : '▸'}</span>
          <span className="shrink-0">{open ? '📂' : '📁'}</span>
          {isEditing ? (
            <InlineName initial={f.name} maxLength={100} onCommit={commitName} onCancel={() => setEditing(null)} />
          ) : (
            <>
              <span className="truncate font-medium text-text-body">{f.name}</span>
              <span className="ml-auto shrink-0 pl-1 text-[9px] text-text-muted">{countProjects(index, f.id)}</span>
            </>
          )}
        </div>,
      )
      if (open) out.push(...renderLevel(f.id, depth + 1))
    }
    const list = [...(index.projects.get(parentId) ?? [])].sort(cmp)
    for (const p of list) {
      const isEditing = editing?.kind === 'project' && editing.id === p.id
      const firstLine = p.memo?.split('\n')[0]
      out.push(
        <div
          key={p.id}
          role="treeitem"
          data-project-id={p.id}
          title={firstLine ? `${p.name}\n${firstLine}` : p.name}
          onClick={() => !isEditing && onOpenProject(p.id)}
          onContextMenu={(e) => projectMenu(e, p)}
          {...dragProps({ kind: 'project', id: p.id })}
          {...dropProps(parentId)}
          className={rowCls}
          style={{
            paddingLeft: indent(depth) + 14,
            ...(currentProject?.id === p.id ? { background: 'var(--bg-row-active)' } : {}),
          }}
        >
          <span className="shrink-0 text-text-secondary">📄</span>
          {isEditing ? (
            <InlineName initial={p.name} maxLength={200} onCommit={commitName} onCancel={() => setEditing(null)} />
          ) : (
            <span className="truncate text-text-body">{p.name}</span>
          )}
        </div>,
      )
    }
    return out
  }

  const tree = renderLevel(null, 0)

  return (
    <div
      data-testid="project-explorer"
      role="tree"
      aria-label="My Projects"
      onContextMenu={rootMenu}
      {...dropProps(null)}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null)
      }}
      className="flex min-h-full flex-col rounded"
      style={dropTarget === ROOT ? { boxShadow: 'inset 0 0 0 1.5px var(--color-accent-purple)' } : undefined}
    >
      <div className="mb-2 flex items-center gap-1">
        <p className="flex-1 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">My Projects</p>
        <button
          type="button"
          title={canWrite ? 'New Folder' : READ_ONLY_TITLE}
          aria-label="New Folder"
          disabled={!canWrite}
          onClick={() => setEditing({ kind: 'new-folder', parentId: null })}
          className="disabled:opacity-40 rounded px-1 py-0.5 text-[11px] text-text-secondary transition-colors hover:bg-bg-input hover:text-text-body"
        >
          ＋📁
        </button>
        <ProjectSortControl sort={sort} onChange={setSort} />
      </div>
      {tree}
      {tree.length === 0 && (
        <p className="px-2 py-1 text-[10px] text-text-muted">No projects yet. Right-click here to add a folder.</p>
      )}
      {children}
      <div className="min-h-6 flex-1" />
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} label="Project actions" />}
    </div>
  )
}
